import { store } from "@redux/store";
import { selectConfigurationReCaptchaSiteKey } from "layout/redux/selectors";
import validateReCaptchaToken$ from "../apis/postReCaptchaToken";

/**
 * A class to manage the integration and functionality of Google reCAPTCHA v2 (invisible mode).
 * This class provides methods to render the reCAPTCHA widget, handle token generation, and validate
 * the reCAPTCHA response.
 */
export default class ReCaptchaToken {
  /**
   * Initializes a new instance of the ReCaptchaToken class.
   * Sets up default properties such as element IDs, site key, and placeholders for the reCAPTCHA widget.
  */
  constructor() {
    this.id = 'reCaptchaToken';
    this.recaptchaPlaceHolderId = 'recaptcha-placeholder';
    this.widgetId = null;
    this.siteKey = selectConfigurationReCaptchaSiteKey(store.getState());
    this.resolveValidation = null;
    this.input = `<input type="hidden" data-qa="re-captcha-token" data-re-captcha-token id="${this.id}">`;
    this.placeHolder = `<div id="${this.recaptchaPlaceHolderId}" style="display:none;"></div>`;
    this.disclaimer = `<span hidden data-re-captcha-legal class="form__legal">This site is protected by reCAPTCHA and the Google <a class="nhs-c-standard-link--underlined" href="https://policies.google.com/privacy">Privacy Policy</a> and <a class="nhs-c-standard-link--underlined" href="https://policies.google.com/terms">Terms of Service</a> apply.</span>`;
    this.reValidationsAttempts = 4;
    this.iframeChallenge = null;
  }

  /**
   * Generates the HTML markup for the reCAPTCHA input and placeholder elements.
   * @returns {string} The HTML string containing the input and placeholder elements.
  */
  getHtml() {
    return `${this.input}${this.placeHolder}${this.disclaimer}`;
  }

  /**
   * Retrieves the DOM element for the reCAPTCHA placeholder.
   * @returns {HTMLElement|null} The placeholder element or null if not found.
  */
  getPlaceHolder() {
    return document.getElementById(this.recaptchaPlaceHolderId);
  }

  /**
   * Retrieves the DOM element for the reCAPTCHA input field.
   * @returns {HTMLInputElement|null} The input element or null if not found.
  */
  getInput() {
    return document.getElementById(this.id);
  }

  /**
   * Sets the value of the reCAPTCHA input field.
   * @param {string} value - The token value to set in the input field.
  */
  setValue(value) {
    this.getInput().value = value;
  }

  /**
   * Gets the current value of the reCAPTCHA input field.
   * @returns {string} The token value stored in the input field.
  */
  getValue() {
    return this.getInput().value;
  }

  /**
   * Disconnects and cleans up all MutationObservers related to the reCAPTCHA challenge.
   *
   * - If the main observer (this.observer) exists, disconnects it and sets it to null.
   * - If the challenge observer (this.challengeObserver) exists, disconnects it and sets it to null.
   *
   * This method should be called to prevent memory leaks and unwanted side effects
   * when the component is destroyed or when reCAPTCHA observers are no longer needed.
   */
  disconnectObservers() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.challengeObserver) {
      this.challengeObserver.disconnect();
      this.challengeObserver = null;
    }
  }

  /**
  *  Resolves the reCAPTCHA validation as successful and stores the token.
  *
  * - Sets the value of the reCAPTCHA input field to the provided token.
  * - Resolves the validation promise with true, indicating success.
  * - Clears the resolveValidation reference to prevent duplicate resolutions.
  *
  * This method is called when the reCAPTCHA token has been successfully validated,
  * either by the backend or after exhausting all validation attempts.
  *
  * @param {string} token - The validated reCAPTCHA token.
  */
  resolveTokenSuccessfully(token) {
    this.setValue(token);
    this.resolveValidation(true);
    this.resolveValidation = null;
  }

  /**
   * Callback function executed when the reCAPTCHA challenge is successfully completed and a token is generated.
   *
   * - Sends the received token to the backend for validation using validateReCaptchaToken$.
   * - If validation succeeds, sets the token value in the input field and resolves the validation promise.
   * - If validation fails and there are remaining attempts, resets and re-executes the reCAPTCHA challenge.
   * - If no attempts remain, resolves the validation promise as successful (fails open).
   * - Handles both success and error cases, ensuring the validation flow continues or retries as needed.
   *
   * @param {string} token - The reCAPTCHA token generated by the user challenge.
  */
  onSuccess(token) {
    this.token = token;
    validateReCaptchaToken$(token)
      .then(result => {
        if (result.Success) {
          this.resolveTokenSuccessfully(token);
        } else if (this.reValidationsAttempts > 1) {
          this.reValidationsAttempts -= 1;
          this.token = null;
          window.grecaptcha.reset(this.widgetId);
          window.grecaptcha.execute(this.widgetId);
        } else if (this.resolveValidation) {
          this.resolveTokenSuccessfully(token);
        }
      })
      .catch(() => {
        if (this.reValidationsAttempts > 1) {
          this.reValidationsAttempts -= 1;
          this.token = null;
          window.grecaptcha.reset(this.widgetId);
          window.grecaptcha.execute(this.widgetId);
        } else if (this.resolveValidation) {
          this.resolveTokenSuccessfully(token);
        }
      });
  }

/**
 * Observes the DOM for the insertion of the reCAPTCHA challenge iframe and sets up a secondary observer
 * to detect when the user closes the challenge without solving it.
 *
 * - Uses a MutationObserver to watch for new iframes with a src containing 'www.google.com/recaptcha/api2/bframe'.
 * - When the challenge iframe is found, attaches another MutationObserver to its container to monitor style changes.
 * - If the challenge container becomes hidden (opacity 0 and not full height) while a validation is pending,
 *   resolves the validation as unsuccessful (false).
 * - Ensures proper cleanup by disconnecting observers when no longer needed.
 *
 * This method helps detect when the user closes the reCAPTCHA challenge dialog without completing it,
 * allowing the application to handle such cases gracefully.
 */
  startChallengeObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.tagName === 'IFRAME' && node.src?.includes('www.google.com/recaptcha/api2/bframe')) {
            this.iframeChallenge = node;
            
            const challengeContainer = node.parentElement?.parentElement;
            if (challengeContainer) {
              this.challengeObserver = new MutationObserver(() => {
                const isHidden =
                  node.style.height !== '100%' &&
                  challengeContainer.style.opacity === '0';

                if (isHidden && this.resolveValidation && !this.token) {
                  this.resolveValidation(false);
                  this.resolveValidation = null;
                }
              });

              this.challengeObserver.observe(challengeContainer, {
                attributes: true,
                attributeFilter: ['style']
              });
            }
          }
        });
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Binds the reCAPTCHA widget to the placeholder element and sets up the callback for token generation.
   * This method initializes the widget using the Google reCAPTCHA API.
  */
  bindEvents() {
    this.widgetId = window.grecaptcha?.render?.(this.getPlaceHolder(), {
      sitekey: this.siteKey,
      callback: this.onSuccess.bind(this),
      size: 'invisible'
    });

    this.startChallengeObserver();
  }

  /**
   * This method should be called when the component is being destroyed
   * to prevent memory leaks and unwanted side effects.
  */
  unbindEvents() {
    this.disconnectObservers();
    
    if (this.resolveValidation) {
      this.resolveValidation(false);
      this.resolveValidation = null;
    }

    if (this.iframeChallenge) {
      this.iframeChallenge.remove();
    }
  }

  /**
   * Validates the reCAPTCHA response.
   * If the reCAPTCHA token is not already generated, it triggers the reCAPTCHA execution.
   * @returns {Promise<boolean>} A promise that resolves to true if validation is successful, otherwise false.
  */
  validate() {
    const input = this.getInput();
    const isRequired = input?.hasAttribute('required');
    const isWidgetIdValid = this.widgetId !== null && this.widgetId !== undefined && typeof this.widgetId === 'number';
    const isGrecaptchaAvailable = typeof window.grecaptcha?.execute === 'function';

    if (!isRequired || !isGrecaptchaAvailable || !isWidgetIdValid || this.getValue()) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.resolveValidation = resolve;
      window.grecaptcha.execute(this.widgetId);
    });
  }
}
